import type { JSONContent } from "@tiptap/core"
import type { SavedIdList } from "articulated"
import type { CourseColor } from "./colors"
import type { Permission } from "./permissions"

export interface CardStack {
  id: string
  type: "card-stack"
  attributes: CardStackAttributes
  relationships: CardStackRelationships
  meta: CardStackMeta
}

export type CardStackAttributes = UnifiedCardStackAttributes | LegacyCardStackAttributes

export type UnifiedCardStack = CardStack & { attributes: UnifiedCardStackAttributes }
export type LegacyCardStack = CardStack & { attributes: LegacyCardStackAttributes }

interface CardStackRelationships {
  course: null | { data: CourseLinkage | null }
  planbook: { data: PlanbookLinkage }
}

interface CourseLinkage {
  type: "course"
  id: string
}
interface PlanbookLinkage {
  type: "planbook"
  id: string
}

export interface CardStackMeta {
  permissions: Array<Permission>
  isNewLesson?: boolean
  cardsUpdatingFromTemplate?: boolean
  /**
   * `${templateId}:${_revision}` of the course template this lesson is currently carrying, so a
   * re-render can tell it is already up to date.
   */
  appliedTemplate?: string
  /**
   * For a phantom lesson, its owner and dateString, which identifies the phantom's slot.
   *
   * Routine: ownerId <- planbookId; lesson: ownerId <- courseId.
   */
  phantomFor?: { ownerId: string; dateString: string }
}

// ----------
// CardStack attributes
// ----------

interface CardStackAttributesBase {
  /**
   * In a UnifiedCardStack, this is extracted from `proseMirror`,
   * unless the template has not yet been applied - then this is
   * the title's temporary source of truth.
   */
  title: string | null
  /** Only used for units. */
  color: CourseColor | null
  parentCardStack?: null | ParentCardStack
  cachedDates?: Array<string>
  /**
   * Comments on the lesson itself (from the Instructional Hub).
   */
  commentIds: string[]
  version: number
  versionNarrative?: object
  _revision: number
  _revisedBySessionId: string | null
}

export interface CardStackExtracted {
  standards: LessonStandard[]
  cardCommentIds: string[]
  // TODO
  // // Tag usages that appear directly in the text (not in a comment thread).
  // // For searching and aggregate statistics.
  // // You'll also need to search + aggregate tag usages in comment threads
  // // targeting this lesson, taking care to apply permissions.
  // tagUsages: Array<TagUsage>
  // mentionedUserIds: Array<string>
}

export interface UnifiedCardStackAttributes extends CardStackAttributesBase {
  schemaVersion: 2
  usedAs: UnifiedCardStackUsedAsTypes
  /**
   * Serialized ProseMirror content (ProseMirror JSON + Articulated character IDs).
   * Semi-opaque: the structure of blocks is clear in the JSON, but blocks' HTML
   * contents and the character IDs are opaque.
   * Excludes the lesson title, unlike UnifiedCardStackEditor's Tiptap instance;
   * the title is joined/separated in UnifiedCardStackEditor.
   *
   * null if the template has not yet been applied.
   *
   * Note: The DB may store { migrateFromCards: true } here instead of actual content,
   * but that is hidden from the frontend by the CardStack adapter.
   */
  proseMirror: ProseMirrorContent | null
  extracted: CardStackExtracted
  /**
   * We extract a simplified view of `proseMirror.json` to here for consumption in other places.
   * Eventually, this will be replaced with `extracted` fields.
   *
   * null if the template has not yet been applied.
   */
  cards: Array<LessonCard> | null
}

export type UnifiedCardStackUsedAsTypes = "lesson" | "lesson-template" | "unit"

export interface ProseMirrorContent {
  json: JSONContent
  ids: SavedIdList
}

/**
 * The lesson this one was copied from: another lesson, a template, the matching lesson of the
 * planbook this one was copied from, or a lesson from the class's linked class.
 */
export interface ParentCardStack {
  id: string
  version: number
  ownerIds: Array<string> | null
  courseId: string | null
  planbookId: string | null
  /** null for unknown (created before we added this field). */
  source: "paste" | "template" | "copy-planbook" | "follow" | null
}

// ----------
// Legacy CardStack attributes
// ----------

export interface LegacyCardStackAttributes extends CardStackAttributesBase {
  // For unknown reasons, some stored CardStacks have null/missing schemaVersion.
  schemaVersion: 1 | null
  usedAs: LegacyCardStackUsedAsTypes
  /**
   * null if the CardStack only has a title (no template applied yet).
   */
  cards: Array<Card> | null

  // Show Ecto defaults so that TypeScript lets us read these unconditionally.
  proseMirror: null
  extracted: null
}

export type LegacyCardStackUsedAsTypes =
  | "lesson"
  | "lesson-template"
  | "routine"
  | "unit"
  | "routine-template"

export interface LegacyLessonCardStackAttributes extends LegacyCardStackAttributes {
  cards: Array<LessonCard>
}

export interface LegacyLessonCardStack extends CardStack {
  attributes: LegacyLessonCardStackAttributes
}

export type Card =
  | CardCourseSlot
  | CardTitleValueHtml
  | CardValueHtml
  | CardStandards
  | CardGoogleClassroom

export type LessonCard = CardTitleValueHtml | CardStandards | CardGoogleClassroom | CardValueHtml

export interface CardTitleValueHtml {
  id: string
  type: "card-title-value-html"
  attributes: CardAttributesTitleValueHtml
}

/**
 * What we call a "Day Card" in the app. It can appear on a lesson
 * and at one point, you used to be drag them into lessons (Cc v3).
 * Right now, it only is on a routine/routine-template
 */
export interface CardValueHtml {
  id: string
  type: "card-value-html"
  attributes: CardAttributesValueHtml
}
export interface CardGoogleClassroom {
  id: string
  type: "card-google-classroom"
  attributes: CardAttributesGoogleClassroom
}

/**
 * How we know where to show a course in the day.
 * Does not appear on lessons
 */
export interface CardCourseSlot {
  id: string
  type: "card-course-slot"
  attributes: CardAttributesCourseSlot
}
export interface CardStandards {
  id: string
  type: "card-standards"
  attributes: CardAttributesStandards
}

export const CARD_COLORS = [
  "black",
  "yellow",
  "orange",
  "red",
  "magenta",
  "purple",
  "navy",
  "blue",
  "teal",
  "green",
] as const

export type CardColorType = (typeof CARD_COLORS)[number]

export interface CardAttributesBase {
  position: number | null
  attachments: Array<CardAttachment>
  /** Ids of comments on this card. Note that the current user may not have access to all of them. */
  commentIds: Array<string>
  /** Legacy embedded comments. */
  comments: Array<LegacyCardComment>
  color: CardColorType
  /** Determines whether the card shows up on the class website */
  isPublic: boolean
  parentCardId: string | null
}

export interface CardAttributesTitleValueHtml extends CardAttributesBase {
  title: string
  value: string
}

export interface CardAttributesValueHtml extends CardAttributesBase {
  value: string
}

export type GoogleClassroomPostType =
  | "ASSIGNMENT"
  | "ANNOUNCEMENT"
  | "MATERIAL"
  | "SHORT_ANSWER_QUESTION"
  | "MULTIPLE_CHOICE_QUESTION"

export interface CardAttributesGoogleClassroom extends CardAttributesBase {
  postType: GoogleClassroomPostType
  title: string
  value: string
  maxPoints: string | null
  classSettings: Array<GoogleClassroomClassSetting>
  attachmentSettings: AttachmentSettingsObject
  extraPostProperties: {
    topicName?: string | null
    choices?: Array<string> | null
  }
  /**
   * We use this for querying on the backend
   * ```
   * map(classSettings, setting => setting.googleClassId) === enabledGoogleClassIds
   * ```
   */
  enabledGoogleClassIds: Array<string>
  /* We keep track of this for this card so we know when to update it in GC*/
  _revision: number
}

export interface AttachmentSettingsObject {
  [id: string]: { shareMode: ShareModeSettings }
}

export type ShareModeSettings = "VIEW" | "EDIT" | "STUDENT_COPY"

export type RelativeDueDate = "SAME_DAY" | "NEXT_DAY" | "END_OF_WEEK" | "END_OF_NEXT_WEEK"

export interface GoogleClassroomClassSetting {
  googleClassId: string
  /**
   * `isEnabled` accounts for a quick way to check if the card is scheduled or not
   * Then if the user turns the class off we can save their preferred time settings
   * without having them re-enter them
   */
  isEnabled: boolean
  scheduledAtTime: {
    hour: number
    minute: number
  }
  dueAt: null | {
    date: string | RelativeDueDate
    hour: number
    minute: number
  }
}

export interface CardAttributesStandards extends CardAttributesBase {
  title: string
  standards: Array<CardStandard>
}

export interface CardAttributesCourseSlot {
  courseId: string
  position: number | null
  parentCardId: string | null
}

/**
 * A standard as stored in a lesson. {@link CardStandard} adds card-specific emphasis on top.
 */
export interface LessonStandard {
  id: string
  code: string | null
  statement: string
  jurisdiction: string
  grades: string
  standardSetId: string
  subject: string
}

export interface CardStandard extends LessonStandard {
  emphasisLevel: null | "STARRED" | "EXPANDED"
}

export interface CardAttachment {
  id: string
  title: string
  url: string
  type: string
  /** Null for links and Drive/Dropbox picks — only real uploads carry one. */
  mimetype: string | null
  /** Null for links and Drive/Dropbox picks — only real uploads carry one. */
  size: number | null
  isWriteable: boolean
}

/**
 * Legacy embedded comments (Spikey.Card.Comment).
 * 2025-26 comments use Comment (Spikey.Comment), its own collection.
 * New CardStacks (unified editor) use Annotations instead.
 */
export interface LegacyCardComment {
  id: string
  text: string
  commenterName: string
  commenterId: string
  createdAt: string
}

// ----------
// CardStackSummary
// ----------

export interface CardStackSummary {
  id: string
  type: "card-stack-summary"
  attributes: CardStackSummaryAttributes
  meta: { permissions?: Array<Permission> } & Record<string, any>
}

interface CardStackSummaryAttributes {
  /** Null until a first edit names the lesson — phantom summaries are born untitled. */
  title: string | null
  /** Only used for units. */
  color: CourseColor | null
  usedAs?: "lesson" | "routine"
  /** Null on a phantom summary that has never been persisted. */
  _revision: number | null
}
